import React, { useState } from 'react';
import { hashPassword, secureCleanup } from '../utils/crypto';
import { useUser } from './UserProvider';
import '../css.css';

/**
 * 관리자 암호 입력 모달 컴포넌트
 * @param {Object} props
 * @param {boolean} props.isOpen - 모달 표시 여부
 * @param {Function} props.onClose - 모달 닫기 핸들러
 * @param {Function} props.onSuccess - 인증 성공 핸들러
 */
const AdminPasswordModal = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useUser();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [attemptCount, setAttemptCount] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password.trim()) {
      setError('암호를 입력해주세요.');
      return;
    }

    if (!user?.username) {
      setError('사용자 정보를 찾을 수 없습니다.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      // 클라이언트 측에서 비밀번호 해싱
      const hashedPassword = hashPassword(password, user.username, 'login');
      
      const response = await fetch('/api/admin/verify-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ hashedPassword })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // adminToken 저장
        localStorage.setItem('adminToken', data.adminToken);
        
        // 성공 후 상태 초기화
        setPassword('');
        setError('');
        setAttemptCount(0);
        
        // 메모리 정리
        const passwordCopy = password;
        secureCleanup({ passwordCopy }, ['passwordCopy']);
        
        // 모달 닫기 전에 성공 콜백 호출
        if (onSuccess) {
          onSuccess();
        }
        
        onClose();
      } else {
        const newAttemptCount = attemptCount + 1;
        setAttemptCount(newAttemptCount);
        
        // 3회 이상 실패 시 adminToken 삭제 및 모달 닫기
        if (newAttemptCount >= 3) {
          localStorage.removeItem('adminToken');
          setPassword('');
          setError('');
          setAttemptCount(0);
          
          alert('암호 입력을 3회 이상 실패했습니다.\n관리자 권한이 해제됩니다.');
          onClose();
        } else {
          setError(data.error || `암호가 올바르지 않습니다. (${newAttemptCount}/3)`);
          // 실패 시 암호 입력란 유지 (재입력을 위해)
        }
      }
    } catch (err) {
      console.error('관리자 인증 오류:', err);
      setError('인증 중 오류가 발생했습니다.');
      setPassword(''); // 에러 시에도 암호 초기화
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // 취소 버튼 클릭 시 adminToken 삭제
    localStorage.removeItem('adminToken');
    setPassword('');
    setError('');
    setAttemptCount(0);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="admin-password-modal">
        <div className="modal-header">
          <h3>🔐 관리자 권한 확인</h3>
          <button className="modal-close-btn" onClick={handleClose}>
            ✕
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="modal-content">
            <p className="admin-password-description">
              관리자 기능에 접근하려면 관리자 암호를 입력해주세요.
            </p>
            
            <div className="admin-password-input-group">
              <label htmlFor="admin-password">관리자 암호</label>
              <input
                id="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="관리자 암호 입력"
                className="admin-password-input"
                autoFocus
                disabled={loading}
              />
            </div>

            {error && (
              <div className="admin-password-error">
                ⚠️ {error}
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={handleClose}
              disabled={loading}
            >
              취소
            </button>
            <button
              type="submit"
              className="btn-confirm"
              disabled={loading || !password.trim()}
            >
              {loading ? '확인 중...' : '확인'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminPasswordModal;
